import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogTrigger } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { ArrowLeft, Swords, Sparkles, RefreshCw, Trash2, Plus, Target, Eye, Edit, Save, X, Check, ChevronLeft, ChevronRight, RotateCcw, Users, BarChart3, Trophy } from 'lucide-react';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';

const DuelManager = () => {
  const { classroomId } = useParams();
  const navigate = useNavigate();
  
  const [duels, setDuels] = useState([]);
  const [loading, setLoading] = useState(true);
  const [classroom, setClassroom] = useState(null);
  
  // Create duel states
  const [duelDialogOpen, setDuelDialogOpen] = useState(false);
  const [creatingDuel, setCreatingDuel] = useState(false);
  const [duelForm, setDuelForm] = useState({
    title: '',
    subject: '',
    thematic_context: '',
    difficulty_level: 'Aplicação'
  });
  
  // View/Edit questions states
  const [viewDuelDialogOpen, setViewDuelDialogOpen] = useState(false);
  const [selectedDuel, setSelectedDuel] = useState(null);
  const [editMode, setEditMode] = useState(false);
  const [editedQuestions, setEditedQuestions] = useState([]);
  const [currentQuestionIndex, setCurrentQuestionIndex] = useState(0);
  const [savingDuel, setSavingDuel] = useState(false);

  // Duel performance report
  const [reportDialogOpen, setReportDialogOpen] = useState(false);
  const [reportDuel, setReportDuel] = useState(null);
  const [reportData, setReportData] = useState(null);
  const [loadingReport, setLoadingReport] = useState(false);

  const handleOpenReport = async (duel) => {
    setReportDuel(duel);
    setReportDialogOpen(true);
    setReportData(null);
    setLoadingReport(true);
    try {
      const res = await fetch(`${BACKEND_URL}/api/duels/${duel.duel_id}/report`, { credentials: 'include' });
      if (!res.ok) throw new Error('Falha ao carregar relatório');
      setReportData(await res.json());
    } catch (e) {
      toast.error(e.message || 'Erro ao carregar relatório');
    } finally {
      setLoadingReport(false);
    }
  };

  useEffect(() => {
    loadData();
  }, [classroomId]);

  const loadData = async () => {
    setLoading(true);
    try {
      // Load classroom
      const classroomRes = await fetch(`${BACKEND_URL}/api/classrooms/${classroomId}`, {
        credentials: 'include'
      });
      if (classroomRes.ok) {
        const data = await classroomRes.json();
        setClassroom(data);
      }
      
      // Load duels
      const duelsRes = await fetch(`${BACKEND_URL}/api/duels/classroom/${classroomId}`, {
        credentials: 'include'
      });
      if (duelsRes.ok) {
        const data = await duelsRes.json();
        setDuels(data);
      }
    } catch (error) {
      console.error('Failed to load data:', error);
      toast.error('Erro ao carregar dados');
    } finally {
      setLoading(false);
    }
  };

  const handleCreateDuel = async () => {
    if (!duelForm.title || !duelForm.subject || !duelForm.thematic_context) {
      toast.error('Preencha todos os campos obrigatórios');
      return;
    }
    
    setCreatingDuel(true);
    try {
      const response = await fetch(`${BACKEND_URL}/api/duels/create`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ ...duelForm, classroom_id: classroomId })
      });
      
      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.detail || 'Failed to create duel');
      }
      
      toast.success('Arena de Duelo criada com sucesso! ⚔️', { duration: 4000 });
      setDuelDialogOpen(false);
      setDuelForm({ title: '', subject: '', thematic_context: '', difficulty_level: 'Aplicação' });
      loadData();
    } catch (error) {
      console.error('Failed to create duel:', error);
      toast.error(error.message || 'Erro ao criar duelo');
    } finally {
      setCreatingDuel(false);
    }
  };

  const handleDeleteDuel = async (duelId) => {
    if (!window.confirm('Tem certeza que deseja excluir esta arena de duelo?')) return;
    
    try {
      const response = await fetch(`${BACKEND_URL}/api/duels/${duelId}`, {
        method: 'DELETE',
        credentials: 'include'
      });
      
      if (!response.ok) throw new Error('Failed to delete duel');
      
      toast.success('Duelo excluído!');
      loadData();
    } catch (error) {
      console.error('Failed to delete duel:', error);
      toast.error('Erro ao excluir duelo');
    }
  };

  const handleClearParticipations = async (duelId, title) => {
    if (!window.confirm(`Limpar todas as participações de "${title}"? Isso permitirá que todos os alunos participem novamente.`)) return;
    try {
      const response = await fetch(`${BACKEND_URL}/api/duels/${duelId}/clear-participations`, {
        method: 'POST',
        credentials: 'include'
      });
      if (!response.ok) throw new Error('Failed');
      const data = await response.json();
      toast.success(data.message);
      loadData();
    } catch (error) {
      toast.error('Erro ao limpar participações');
    }
  };

  // View duel questions
  const handleViewDuel = async (duel) => {
    try {
      const response = await fetch(`${BACKEND_URL}/api/duels/${duel.duel_id}`, {
        credentials: 'include'
      });
      
      if (!response.ok) throw new Error('Failed to load duel');
      
      const fullDuel = await response.json();
      setSelectedDuel(fullDuel);
      setEditedQuestions(JSON.parse(JSON.stringify(fullDuel.questions || [])));
      setCurrentQuestionIndex(0);
      setEditMode(false);
      setViewDuelDialogOpen(true);
    } catch (error) {
      console.error('Failed to load duel:', error);
      toast.error('Erro ao carregar detalhes do duelo');
    }
  };

  // Save edited questions
  const handleSaveQuestions = async () => {
    setSavingDuel(true);
    try {
      const response = await fetch(`${BACKEND_URL}/api/duels/${selectedDuel.duel_id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ questions: editedQuestions })
      });
      
      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.detail || 'Failed to save');
      }
      
      toast.success('Questões salvas com sucesso!');
      setEditMode(false);
      setSelectedDuel({ ...selectedDuel, questions: editedQuestions });
      loadData();
    } catch (error) {
      console.error('Failed to save questions:', error);
      toast.error(error.message || 'Erro ao salvar questões');
    } finally {
      setSavingDuel(false);
    }
  };

  // Update a specific question field
  const updateQuestion = (field, value) => {
    const updated = [...editedQuestions];
    updated[currentQuestionIndex] = {
      ...updated[currentQuestionIndex],
      [field]: value
    };
    setEditedQuestions(updated);
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-slate-50 to-slate-100 flex items-center justify-center">
        <RefreshCw className="w-8 h-8 animate-spin text-red-500" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-b from-slate-50 to-slate-100">
      <div className="max-w-5xl mx-auto p-6">
        {/* Header */}
        <div className="flex items-center justify-between mb-8">
          <div className="flex items-center gap-4">
            <Button
              variant="ghost"
              onClick={() => navigate(`/teacher/classroom/${classroomId}`)}
              className="rounded-full"
            >
              <ArrowLeft className="w-5 h-5" />
            </Button>
            <div>
              <h1 className="text-2xl font-outfit font-bold text-slate-900 flex items-center gap-2">
                <Swords className="w-7 h-7 text-red-500" />
                Arenas de Duelo
              </h1>
              <p className="text-slate-500 text-sm">{classroom?.name}</p>
            </div>
          </div>
          
          {/* Create Duel Button */}
          <Dialog open={duelDialogOpen} onOpenChange={setDuelDialogOpen}>
            <DialogTrigger asChild>
              <Button
                data-testid="create-duel-btn"
                className="bg-gradient-to-r from-red-500 to-orange-500 hover:from-red-600 hover:to-orange-600 text-white"
              >
                <Plus className="w-4 h-4 mr-2" />
                Criar Arena de Duelo
              </Button>
            </DialogTrigger>
            <DialogContent className="max-w-lg">
              <DialogHeader>
                <DialogTitle className="font-outfit flex items-center gap-2">
                  <Swords className="w-5 h-5 text-red-500" />
                  Nova Arena de Duelo
                </DialogTitle>
                <DialogDescription>
                  Crie uma arena para duelos entre alunos com 10 questões geradas por IA.
                </DialogDescription>
              </DialogHeader>
              
              <div className="space-y-4">
                <div>
                  <Label htmlFor="duel-title">Título do Duelo *</Label>
                  <Input
                    id="duel-title"
                    value={duelForm.title}
                    onChange={(e) => setDuelForm({...duelForm, title: e.target.value})}
                    placeholder="Ex: Duelo de Matemática - Equações"
                    data-testid="duel-title-input"
                  />
                </div>
                
                <div>
                  <Label htmlFor="duel-subject">Matéria *</Label>
                  <Input
                    id="duel-subject"
                    value={duelForm.subject}
                    onChange={(e) => setDuelForm({...duelForm, subject: e.target.value})}
                    placeholder="Ex: Matemática, Ciências, História..."
                    data-testid="duel-subject-input"
                  />
                </div>
                
                <div>
                  <Label htmlFor="duel-context">Contextualização Temática *</Label>
                  <Textarea
                    id="duel-context"
                    value={duelForm.thematic_context}
                    onChange={(e) => setDuelForm({...duelForm, thematic_context: e.target.value})}
                    placeholder="Ex: Batalha Pokémon em um Ginásio de Fogo, aventura espacial..."
                    rows={2}
                    data-testid="duel-context-input"
                  />
                </div>
                
                <div>
                  <Label htmlFor="duel-difficulty">Taxonomia de Bloom</Label>
                  <select
                    id="duel-difficulty"
                    value={duelForm.difficulty_level}
                    onChange={(e) => setDuelForm({...duelForm, difficulty_level: e.target.value})}
                    className="w-full h-10 px-3 border border-slate-200 rounded-lg bg-white text-sm focus:outline-none focus:ring-2 focus:ring-red-500"
                    data-testid="duel-difficulty-select"
                  >
                    <option value="Conhecimento">Conhecimento (Básico)</option>
                    <option value="Compreensão">Compreensão</option>
                    <option value="Aplicação">Aplicação</option>
                    <option value="Análise">Análise</option>
                    <option value="Síntese">Síntese</option>
                    <option value="Avaliação">Avaliação (Avançado)</option>
                  </select>
                </div>
                
                <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 text-sm">
                  <div className="font-semibold text-amber-800 mb-1">⚔️ Regras do Duelo:</div>
                  <ul className="text-amber-700 space-y-1 text-xs">
                    <li>• 10 questões geradas por IA</li>
                    <li>• 45 segundos por questão</li>
                    <li>• 5 corações por jogador</li>
                    <li>• Vencedor: +200 AURA + Insígnia do Conhecimento</li>
                    <li>• Perdedor: -20 HP</li>
                    <li>• 1 participação por duelo</li>
                  </ul>
                </div>
                
                <Button
                  onClick={handleCreateDuel}
                  disabled={creatingDuel}
                  className="w-full bg-gradient-to-r from-red-500 to-orange-500 hover:from-red-600 hover:to-orange-600"
                  data-testid="create-duel-submit-btn"
                >
                  {creatingDuel ? (
                    <>
                      <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                      Gerando questões com IA...
                    </>
                  ) : (
                    <>
                      <Sparkles className="w-4 h-4 mr-2" />
                      Criar Arena de Duelo
                    </>
                  )}
                </Button>
              </div>
            </DialogContent>
          </Dialog>
        </div>

        {/* Duels List */}
        {duels.length === 0 ? (
          <div className="bg-white rounded-2xl border-2 border-slate-100 p-12 text-center">
            <Swords className="w-16 h-16 text-slate-300 mx-auto mb-4" />
            <h3 className="text-lg font-semibold text-slate-700 mb-2">Nenhuma arena de duelo</h3>
            <p className="text-slate-500 text-sm mb-4">
              Crie arenas de duelo para seus alunos competirem em batalhas de conhecimento!
            </p>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {duels.map((duel) => (
              <div 
                key={duel.duel_id}
                className="bg-gradient-to-br from-red-50 via-orange-50 to-amber-50 rounded-2xl border-2 border-red-200 p-6 shadow-lg hover:shadow-xl transition-shadow"
                data-testid={`duel-card-${duel.duel_id}`}
              >
                <div className="flex justify-between items-start mb-4">
                  <div className="flex items-center gap-3">
                    <div className="w-12 h-12 bg-gradient-to-br from-red-500 to-orange-500 rounded-xl flex items-center justify-center shadow-md">
                      <Swords className="w-6 h-6 text-white" />
                    </div>
                    <div>
                      <h3 className="font-bold text-slate-800 text-lg">{duel.title}</h3>
                      <p className="text-sm text-slate-600">{duel.subject}</p>
                    </div>
                  </div>
                  <span className={`px-3 py-1 rounded-full text-xs font-bold ${
                    duel.status === 'available' 
                      ? 'bg-green-100 text-green-700 border border-green-200' 
                      : 'bg-slate-100 text-slate-600'
                  }`}>
                    {duel.status === 'available' ? '⚔️ Disponível' : duel.status}
                  </span>
                </div>
                
                <div className="mb-4">
                  <p className="text-sm text-slate-600 line-clamp-2">{duel.thematic_context}</p>
                </div>
                
                <div className="flex flex-wrap gap-2 mb-4">
                  <span className="px-3 py-1 bg-amber-100 text-amber-700 text-xs font-semibold rounded-full">
                    {duel.difficulty_level}
                  </span>
                  <span className="px-3 py-1 bg-purple-100 text-purple-700 text-xs font-semibold rounded-full">
                    <Target className="w-3 h-3 inline mr-1" />
                    10 questões
                  </span>
                  {duel.participation_count > 0 && (
                    <span className="px-3 py-1 bg-blue-100 text-blue-700 text-xs font-semibold rounded-full">
                      <Users className="w-3 h-3 inline mr-1" />
                      {duel.participation_count} participação(ões)
                    </span>
                  )}
                </div>
                
                {/* Footer: date + action buttons.
                    Buttons live on their own row and wrap gracefully so they
                    never overflow the card, no matter how many are visible.
                    (Cards get up to 4 buttons: Ver Relatório, Limpar
                    Participações, Ver Questões, Excluir.) */}
                <div className="pt-4 border-t border-red-200 space-y-3">
                  <span className="block text-xs text-slate-500">
                    Criado em {new Date(duel.created_at).toLocaleDateString('pt-BR')}
                  </span>
                  <div className="flex flex-wrap gap-2 -mx-1">
                    {duel.participation_count > 0 && (
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => handleOpenReport(duel)}
                        className="text-emerald-700 hover:text-emerald-800 hover:bg-emerald-100"
                        data-testid={`report-duel-${duel.duel_id}`}
                      >
                        <BarChart3 className="w-4 h-4 mr-1" />
                        Ver Relatório
                      </Button>
                    )}
                    {duel.participation_count > 0 && (
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => handleClearParticipations(duel.duel_id, duel.title)}
                        className="text-amber-600 hover:text-amber-700 hover:bg-amber-100"
                        data-testid={`clear-participations-${duel.duel_id}`}
                      >
                        <RotateCcw className="w-4 h-4 mr-1" />
                        Limpar Participações
                      </Button>
                    )}
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => handleViewDuel(duel)}
                      className="text-blue-600 hover:text-blue-700 hover:bg-blue-100"
                      data-testid={`view-duel-${duel.duel_id}`}
                    >
                      <Eye className="w-4 h-4 mr-1" />
                      Ver Questões
                    </Button>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => handleDeleteDuel(duel.duel_id)}
                      className="text-red-500 hover:text-red-700 hover:bg-red-100"
                      data-testid={`delete-duel-${duel.duel_id}`}
                    >
                      <Trash2 className="w-4 h-4 mr-1" />
                      Excluir
                    </Button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}

        {/* View/Edit Questions Dialog */}
        <Dialog open={viewDuelDialogOpen} onOpenChange={(open) => {
          if (!open) {
            setEditMode(false);
          }
          setViewDuelDialogOpen(open);
        }}>
          <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle className="font-outfit flex items-center gap-2">
                <Swords className="w-5 h-5 text-red-500" />
                {selectedDuel?.title}
              </DialogTitle>
              <DialogDescription>
                {editMode ? 'Editando questões do duelo' : 'Visualizando questões do duelo'}
              </DialogDescription>
            </DialogHeader>
            
            {selectedDuel && editedQuestions.length > 0 && (
              <div className="space-y-4">
                {/* Question Navigation */}
                <div className="flex items-center justify-between bg-slate-100 rounded-lg p-3">
                  <Button
                    variant="ghost"
                    size="sm"
                    disabled={currentQuestionIndex === 0}
                    onClick={() => setCurrentQuestionIndex(prev => prev - 1)}
                  >
                    <ChevronLeft className="w-4 h-4 mr-1" />
                    Anterior
                  </Button>
                  <span className="font-semibold text-slate-700">
                    Questão {currentQuestionIndex + 1} de {editedQuestions.length}
                  </span>
                  <Button
                    variant="ghost"
                    size="sm"
                    disabled={currentQuestionIndex === editedQuestions.length - 1}
                    onClick={() => setCurrentQuestionIndex(prev => prev + 1)}
                  >
                    Próxima
                    <ChevronRight className="w-4 h-4 ml-1" />
                  </Button>
                </div>

                {/* Question dots navigation */}
                <div className="flex justify-center gap-2 flex-wrap">
                  {editedQuestions.map((_, idx) => (
                    <button
                      key={idx}
                      onClick={() => setCurrentQuestionIndex(idx)}
                      className={`w-8 h-8 rounded-full text-xs font-bold transition-all ${
                        idx === currentQuestionIndex
                          ? 'bg-red-500 text-white scale-110'
                          : 'bg-slate-200 text-slate-600 hover:bg-slate-300'
                      }`}
                    >
                      {idx + 1}
                    </button>
                  ))}
                </div>

                {/* Current Question */}
                <div className="bg-gradient-to-br from-slate-50 to-slate-100 rounded-xl p-4 border border-slate-200">
                  {/* Question Text */}
                  <div className="mb-4">
                    <Label className="text-sm font-semibold text-slate-600 mb-2 block">Enunciado:</Label>
                    {editMode ? (
                      <Textarea
                        value={editedQuestions[currentQuestionIndex].question_text}
                        onChange={(e) => updateQuestion('question_text', e.target.value)}
                        className="bg-white"
                        rows={3}
                      />
                    ) : (
                      <p className="text-slate-800 bg-white p-3 rounded-lg border">
                        {editedQuestions[currentQuestionIndex].question_text}
                      </p>
                    )}
                  </div>

                  {/* Options */}
                  <div className="space-y-3">
                    {['A', 'B', 'C', 'D'].map((option) => {
                      const fieldName = `option_${option.toLowerCase()}`;
                      const isCorrect = editedQuestions[currentQuestionIndex].correct_answer === option;
                      
                      return (
                        <div key={option} className="flex items-center gap-2">
                          <span className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-bold ${
                            isCorrect 
                              ? 'bg-green-500 text-white' 
                              : 'bg-slate-200 text-slate-600'
                          }`}>
                            {option}
                          </span>
                          {editMode ? (
                            <div className="flex-1 flex gap-2">
                              <Input
                                value={editedQuestions[currentQuestionIndex][fieldName]}
                                onChange={(e) => updateQuestion(fieldName, e.target.value)}
                                className="flex-1 bg-white"
                              />
                              <Button
                                variant={isCorrect ? "default" : "outline"}
                                size="sm"
                                onClick={() => updateQuestion('correct_answer', option)}
                                className={isCorrect ? "bg-green-500 hover:bg-green-600" : ""}
                              >
                                {isCorrect ? <Check className="w-4 h-4" /> : "Correta"}
                              </Button>
                            </div>
                          ) : (
                            <div className={`flex-1 p-2 rounded-lg ${
                              isCorrect 
                                ? 'bg-green-100 border-2 border-green-300 text-green-800' 
                                : 'bg-white border border-slate-200'
                            }`}>
                              {editedQuestions[currentQuestionIndex][fieldName]}
                              {isCorrect && <span className="ml-2 text-green-600 font-semibold">(Correta)</span>}
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>

                  {/* Hint */}
                  {editedQuestions[currentQuestionIndex].hint && (
                    <div className="mt-4 p-3 bg-amber-50 rounded-lg border border-amber-200">
                      <Label className="text-xs font-semibold text-amber-700">Dica:</Label>
                      {editMode ? (
                        <Input
                          value={editedQuestions[currentQuestionIndex].hint || ''}
                          onChange={(e) => updateQuestion('hint', e.target.value)}
                          className="mt-1 bg-white"
                        />
                      ) : (
                        <p className="text-amber-800 text-sm">{editedQuestions[currentQuestionIndex].hint}</p>
                      )}
                    </div>
                  )}
                </div>

                {/* Action Buttons */}
                <div className="flex justify-between pt-4 border-t">
                  {editMode ? (
                    <>
                      <Button
                        variant="outline"
                        onClick={() => {
                          setEditedQuestions(JSON.parse(JSON.stringify(selectedDuel.questions)));
                          setEditMode(false);
                        }}
                      >
                        <X className="w-4 h-4 mr-2" />
                        Cancelar
                      </Button>
                      <Button
                        onClick={handleSaveQuestions}
                        disabled={savingDuel}
                        className="bg-green-500 hover:bg-green-600"
                      >
                        {savingDuel ? (
                          <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                        ) : (
                          <Save className="w-4 h-4 mr-2" />
                        )}
                        Salvar Alterações
                      </Button>
                    </>
                  ) : (
                    <>
                      <Button
                        variant="outline"
                        onClick={() => setViewDuelDialogOpen(false)}
                      >
                        Fechar
                      </Button>
                      <Button
                        onClick={() => setEditMode(true)}
                        className="bg-blue-500 hover:bg-blue-600"
                      >
                        <Edit className="w-4 h-4 mr-2" />
                        Editar Questões
                      </Button>
                    </>
                  )}
                </div>
              </div>
            )}
          </DialogContent>
        </Dialog>

        {/* Duel Performance Report Dialog */}
        <Dialog open={reportDialogOpen} onOpenChange={setReportDialogOpen}>
          <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle className="font-outfit flex items-center gap-2">
                <BarChart3 className="w-5 h-5 text-emerald-600" />
                Relatório de Desempenho
              </DialogTitle>
              <DialogDescription>
                {reportDuel?.title} — desempenho individual dos participantes
              </DialogDescription>
            </DialogHeader>

            {loadingReport ? (
              <div className="py-12 flex items-center justify-center text-slate-500" data-testid="report-loading">
                <RefreshCw className="w-5 h-5 animate-spin mr-2" />
                Carregando relatório...
              </div>
            ) : !reportData ? (
              <div className="py-12 text-center text-slate-500 text-sm">Sem dados</div>
            ) : reportData.participants.length === 0 ? (
              <div className="py-12 text-center text-slate-500 text-sm" data-testid="report-empty">
                Nenhum aluno participou deste duelo ainda.
              </div>
            ) : (
              <div className="space-y-4">
                {/* Summary strip */}
                <div className="grid grid-cols-3 gap-3">
                  <div className="rounded-xl border-2 border-emerald-100 bg-emerald-50 p-3 text-center">
                    <p className="text-[11px] uppercase tracking-wider text-emerald-700 font-bold">Alunos</p>
                    <p className="text-2xl font-black text-emerald-800">{reportData.participants.length}</p>
                  </div>
                  <div className="rounded-xl border-2 border-blue-100 bg-blue-50 p-3 text-center">
                    <p className="text-[11px] uppercase tracking-wider text-blue-700 font-bold">Partidas</p>
                    <p className="text-2xl font-black text-blue-800">{reportData.duel.total_matches}</p>
                  </div>
                  <div className="rounded-xl border-2 border-amber-100 bg-amber-50 p-3 text-center">
                    <p className="text-[11px] uppercase tracking-wider text-amber-700 font-bold">Questões</p>
                    <p className="text-2xl font-black text-amber-800">{reportData.duel.total_questions}</p>
                  </div>
                </div>

                {/* Per-student table */}
                <div className="rounded-xl border-2 border-slate-100 overflow-hidden" data-testid="report-table">
                  <table className="w-full text-sm">
                    <thead className="bg-slate-50 text-slate-600">
                      <tr>
                        <th className="text-left px-4 py-3 font-outfit font-bold">Aluno</th>
                        <th className="text-center px-3 py-3 font-outfit font-bold">
                          <span className="inline-flex items-center gap-1"><Check className="w-3.5 h-3.5 text-emerald-600"/> Acertos</span>
                        </th>
                        <th className="text-center px-3 py-3 font-outfit font-bold">
                          <span className="inline-flex items-center gap-1"><X className="w-3.5 h-3.5 text-rose-600"/> Erros</span>
                        </th>
                        <th className="text-center px-3 py-3 font-outfit font-bold">Aproveit.</th>
                        <th className="text-center px-3 py-3 font-outfit font-bold hidden sm:table-cell">Vitórias</th>
                      </tr>
                    </thead>
                    <tbody>
                      {reportData.participants.map((p, i) => {
                        const acc = p.accuracy;
                        const accColor = acc >= 70 ? 'text-emerald-700 bg-emerald-100' : acc >= 40 ? 'text-amber-700 bg-amber-100' : 'text-rose-700 bg-rose-100';
                        return (
                          <tr
                            key={p.student_id}
                            className={`border-t border-slate-100 ${i % 2 === 1 ? 'bg-slate-50/50' : ''}`}
                            data-testid={`report-row-${p.student_id}`}
                          >
                            <td className="px-4 py-3">
                              <div className="flex items-center gap-2">
                                {i === 0 && <Trophy className="w-4 h-4 text-amber-500 flex-shrink-0" />}
                                <div className="min-w-0">
                                  <p className="font-outfit font-bold text-slate-800 truncate">{p.user_name}</p>
                                  {p.monster_name && (
                                    <p className="text-[11px] text-slate-500 truncate">{p.monster_name}</p>
                                  )}
                                </div>
                              </div>
                            </td>
                            <td className="text-center px-3 py-3 font-black text-emerald-700 tabular-nums" data-testid={`report-correct-${p.student_id}`}>{p.correct}</td>
                            <td className="text-center px-3 py-3 font-black text-rose-600 tabular-nums" data-testid={`report-wrong-${p.student_id}`}>{p.wrong}</td>
                            <td className="text-center px-3 py-3">
                              <span
                                className={`inline-block px-2.5 py-1 rounded-lg font-black text-xs tabular-nums ${accColor}`}
                                data-testid={`report-accuracy-${p.student_id}`}
                              >
                                {acc.toFixed(1)}%
                              </span>
                            </td>
                            <td className="text-center px-3 py-3 text-slate-700 font-bold tabular-nums hidden sm:table-cell">
                              {p.wins}<span className="text-slate-400 text-xs">/{p.matches_played}</span>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
                <p className="text-[11px] text-slate-400 italic text-center">
                  Ordenado por aproveitamento. Empates são resolvidos pelo total de acertos.
                </p>
              </div>
            )}

            <div className="flex justify-end pt-4 border-t">
              <Button variant="outline" onClick={() => setReportDialogOpen(false)}>Fechar</Button>
            </div>
          </DialogContent>
        </Dialog>
      </div>
    </div>
  );
};

export default DuelManager;
